import React, {
  useState,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from 'react';
import {
  validateCardNumber,
  validateExpiry,
  validateCvc,
  getCardBehavior,
  BEHAVIOR_MESSAGES,
} from '../utils/stripeDevMode';

/**
 * DevCardElement - A fake Stripe CardElement for development/testing.
 * Matches Stripe's styling and exposes a similar API for parent components.
 *
 * Usage:
 *   const devCardRef = useRef();
 *   <DevCardElement ref={devCardRef} onChange={handleCardChange} />
 *
 *   // Get card data:
 *   const cardData = devCardRef.current.getCardData();
 */
const DevCardElement = forwardRef(({ onChange, disabled }, ref) => {
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [focused, setFocused] = useState(null);

  // Expose getCardData method to parent
  useImperativeHandle(ref, () => ({
    getCardData: () => ({
      number: cardNumber.replace(/\s/g, ''),
      expiry,
      cvc,
    }),
    clear: () => {
      setCardNumber('');
      setExpiry('');
      setCvc('');
    },
  }));

  // Validate and notify parent
  const notifyChange = useCallback(
    (number, exp, cvcVal) => {
      const normalizedNumber = number.replace(/\s/g, '');
      const isComplete =
        normalizedNumber.length >= 15 && exp.length === 5 && cvcVal.length >= 3;

      let error = null;

      if (isComplete) {
        if (!validateCardNumber(normalizedNumber)) {
          error = { message: 'Your card number is invalid.' };
        } else if (!validateExpiry(exp)) {
          error = { message: "Your card's expiration date is invalid." };
        } else if (!validateCvc(cvcVal)) {
          error = { message: "Your card's security code is invalid." };
        } else {
          // Check for test card behaviors that would fail
          const behavior = getCardBehavior(normalizedNumber);
          if (behavior !== 'success') {
            // Don't show error yet - let it fail on submit
          }
        }
      }

      // Call onChange with Stripe-like event object
      if (onChange) {
        onChange({
          complete: isComplete && !error,
          empty: !normalizedNumber && !exp && !cvcVal,
          error,
          brand: detectCardBrand(normalizedNumber),
        });
      }
    },
    [onChange]
  );

  // Format card number with spaces (4-4-4-4)
  const handleCardNumberChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 16) value = value.slice(0, 16);

    // Format with spaces
    const formatted = value.replace(/(\d{4})(?=\d)/g, '$1 ');
    setCardNumber(formatted);
    notifyChange(value, expiry, cvc);
  };

  // Format expiry (MM/YY)
  const handleExpiryChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 4) value = value.slice(0, 4);

    // Auto-format with slash
    if (value.length >= 2) {
      value = value.slice(0, 2) + '/' + value.slice(2);
    }

    setExpiry(value);
    notifyChange(cardNumber, value, cvc);
  };

  // CVC input
  const handleCvcChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 4) value = value.slice(0, 4);

    setCvc(value);
    notifyChange(cardNumber, expiry, value);
  };

  // Detect card brand from number
  const detectCardBrand = (number) => {
    const normalized = number.replace(/\s/g, '');
    if (/^4/.test(normalized)) return 'visa';
    if (/^5[1-5]/.test(normalized)) return 'mastercard';
    if (/^3[47]/.test(normalized)) return 'amex';
    if (/^6(?:011|5)/.test(normalized)) return 'discover';
    return 'unknown';
  };

  // Input styling to match Stripe's appearance
  const inputStyle = {
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontFamily:
      'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
    fontSize: '14px',
    color: '#000000',
    padding: 0,
    margin: 0,
  };

  const placeholderColor = '#9ca3af';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        width: '100%',
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      {/* DEV indicator */}
      <span
        style={{
          fontFamily: 'monospace',
          fontSize: '10px',
          fontWeight: 'bold',
          color: '#f59e0b',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          flexShrink: 0,
        }}
      >
        DEV
      </span>

      {/* Card number */}
      <input
        type="text"
        value={cardNumber}
        onChange={handleCardNumberChange}
        onFocus={() => setFocused('number')}
        onBlur={() => setFocused(null)}
        placeholder="Card number"
        disabled={disabled}
        style={{
          ...inputStyle,
          flex: 2,
          minWidth: 0,
        }}
        autoComplete="cc-number"
      />

      {/* Expiry */}
      <input
        type="text"
        value={expiry}
        onChange={handleExpiryChange}
        onFocus={() => setFocused('expiry')}
        onBlur={() => setFocused(null)}
        placeholder="MM/YY"
        disabled={disabled}
        style={{
          ...inputStyle,
          width: '60px',
          flexShrink: 0,
        }}
        autoComplete="cc-exp"
      />

      {/* CVC */}
      <input
        type="text"
        value={cvc}
        onChange={handleCvcChange}
        onFocus={() => setFocused('cvc')}
        onBlur={() => setFocused(null)}
        placeholder="CVC"
        disabled={disabled}
        style={{
          ...inputStyle,
          width: '45px',
          flexShrink: 0,
        }}
        autoComplete="cc-csc"
      />
    </div>
  );
});

DevCardElement.displayName = 'DevCardElement';

export default DevCardElement;
